import { Button, Dialog, DialogActions, DialogContent } from '@mui/material';
import { useContext, useState } from 'react';
import { BarcodeScannerErrorModal } from './BarcodeScannerErrorModal';
import { client } from '../../../../app/services/api/orderManagementClient';
import { UserContext } from '../../../../components/shared/useUser';
import { StockCheckItem } from '../../../../app/services/api/generated';
import { BarcodeStockItemDetails } from '../BarcodeStockItemDetails';
import useLogError from '../../../../hooks/useLogError';
import negativeSound from '../../../../assets/sounds/barcodeNegative.mp3';
import positiveSound from '../../../../assets/sounds/barcodePositive.mp3';
import useSound from 'use-sound';
import { useTranslation } from 'react-i18next';
import { WasmBarcodeScanner2 } from './WasmScanner2';

export const BarcodeScannerModal = (props: { isScannerOpen: boolean, closeModal: () => void, stockCheckItems: StockCheckItem[], setStockCheckItems: React.Dispatch<React.SetStateAction<StockCheckItem[]>>, stockCountName: string | undefined }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isApiErrorModalOpen, setIsApiErrorModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [decodedText, setDecodedText] = useState('');
  const [stockCheck, setStockCheck] = useState<StockCheckItem | undefined>();
  const [isItemDetailsModalOpen, setIsItemDetailsModalOpen] = useState(false);
  const [uom, setUom] = useState('');
  const [scannerLogs, setScannerLogs] = useState<string[]>([]);
  const [barcodes, setBarcodes] = useState<string[]>([]);

  const { logError } = useLogError();

  const { t } = useTranslation('stockCountScanning');

  const [playPositive] = useSound(positiveSound);
  const [playNegative] = useSound(negativeSound);

  const { selectedStore, user } = useContext(UserContext);

  const handlerScannerCapture = (code: string) => {
    if (code != decodedText) { //Code has changed
      setDecodedText(code);
      if (code) { //valid check the api
        handleItemScanned(code);
      }
      setBarcodes([]);
    }
  };

  const handleScannerLog = (message: string) => {
    console.log(message);
    setScannerLogs(prev => [...prev.slice(-9), message]); // Keep last 10 logs
  };

  const handleCloseModal = () => {
    props.closeModal();
  };

  const handleItemScanned = async (itemNumber: string) => {
    if (isLoading) return;

    client.getItemByBarcode(selectedStore?.storeNumber, itemNumber, props.stockCountName, user?.username)
      .then((result) => {
        if (result.data == null) {
          playNegative();
          setIsErrorModalOpen(true);
        }
        else {
          const itemNumber = result.data.sku;
          const stockCheck = props.stockCheckItems.find(x => x.itemNumber === itemNumber);
          const uom = result.data.barcodeUom;

          if (stockCheck) {
            playPositive();
            setStockCheck(stockCheck);
            setUom(uom);
            setIsItemDetailsModalOpen(true);
          }
          else {
            playNegative();
            setIsWarningModalOpen(true);
          }
        }

      })
      .catch((error => {
        logError(error);
        setIsApiErrorModalOpen(true);
      }))
      .finally(() => {
        setIsLoading(false);
      },
      );
  };

  return (<>
    <Dialog open={props.isScannerOpen}
      fullScreen>
      <DialogContent sx={{ p: '0px', pb: '4px', m: '0px', flexGrow: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', alignItems: 'center' }}>
        <WasmBarcodeScanner2 onLog={handleScannerLog}
          onCapture={handlerScannerCapture} />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end', ml: '10px', mr: '10px', mt: '12px', mb: '10px' }}>
        <Button variant='secondary'
          size='md'
          disabled={isLoading}
          onClick={handleCloseModal}>{t('close')}</Button>
      </DialogActions>
    </Dialog>
    <BarcodeStockItemDetails stockCheck={stockCheck}
      isOpen={isItemDetailsModalOpen}
      closeModal={() => setIsItemDetailsModalOpen(false)}
      uom={uom}
      setStockCheckItems={props.setStockCheckItems} />
    <BarcodeScannerErrorModal isOpen={isErrorModalOpen}
      closeModal={() => setIsErrorModalOpen(false)}
      title={t('errorTitle')}
      content={t('errorContent') + decodedText} />
    <BarcodeScannerErrorModal isOpen={isApiErrorModalOpen}
      closeModal={() => setIsApiErrorModalOpen(false)}
      title={t('apiErrorTitle')}
      content={t('apiErrorContent') + decodedText} />
    <BarcodeScannerErrorModal isOpen={isWarningModalOpen}
      closeModal={() => setIsWarningModalOpen(false)}
      title={t('warningTitle')}
      content={t('warningContent')} />
  </>
  );
};